import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { useThemeContext } from '@oberoninternal/travelbase-ds/hooks/useThemeContext';
import { Box, Flex } from '@rebass/grid';
import { gql, NetworkStatus } from '@apollo/client';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import { debounce } from 'debounce';
import React, { useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import activitySearchParams, {
    activitySearchFilterParams,
} from '@oberoninternal/travelbase-website/dist/constants/activitySearchParams';
import parseToDateString from '@oberoninternal/travelbase-website/dist/constants/parseToDateString';
import { FilterCategory } from '@oberoninternal/travelbase-website/dist/entities/Filter';
import {
    ActivitySearchParamsInput,
    TargetAudienceEnum,
    useAgendaFiltersQuery,
} from '@oberoninternal/travelbase-website/dist/generated/graphql';
import { ActivitySearchOrderEnum, useActivitySearchQuery } from '../../generated/graphql';
import composeFilterCategory from '@oberoninternal/travelbase-website/dist/hooks/search/useComposeFilterCategory';
import useFiltersActive from '@oberoninternal/travelbase-website/dist/hooks/useFiltersActive';
import useQueryParams from '@oberoninternal/travelbase-website/dist/hooks/useQueryParams';
import cssCalcBetweenViewport from '@oberoninternal/travelbase-website/dist/utils/cssCalcBetweenViewport';
import ActivityHit from '@oberoninternal/travelbase-website/dist/components/ActivityHit';
import ActivityTile from '@oberoninternal/travelbase-website/dist/components/ActivityTile';
import AgendaHeader, {
    AgendaView,
    Header,
    weekOpts,
} from '@oberoninternal/travelbase-website/dist/components/AgendaHeader';
import ContentWrapper from '@oberoninternal/travelbase-website/dist/components/ContentWrapper';
import WideGrid, { Cell } from '@oberoninternal/travelbase-website/dist/components/designsystem/WideGrid';
import Divider from '@oberoninternal/travelbase-website/dist/components/Divider';
import FilterList from '@oberoninternal/travelbase-website/dist/components/FilterList';
import Formed from '@oberoninternal/travelbase-website/dist/components/Formed';
import Page from '@oberoninternal/travelbase-website/dist/components/Page';
import SearchBottomBar from '@oberoninternal/travelbase-website/dist/components/SearchBottomBar';
import SearchFiltersModal from '@oberoninternal/travelbase-website/dist/components/SearchFiltersModal';
import SearchFilterTags from '@oberoninternal/travelbase-website/dist/components/SearchFilterTags';
import DatepickerField from '@oberoninternal/travelbase-website/dist/components/DatepickerField';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import SearchbarDatepickerInputs from '@oberoninternal/travelbase-website/dist/components/designsystem/datepicker/SearchbarDatepickerInputs';
import addDays from 'date-fns/addDays';
import EventHit from '../EventHit';

export const agendaQuery = gql`
    query ActivitySearch(
        $params: ActivitySearchParamsInput!
        $limit: Int
        $offset: Int
        $startDate: String!
        $endDate: String!
        $order: ActivitySearchOrderEnum!
    ) {
        activities(params: $params, limit: $limit, offset: $offset, order: $order) {
            ... on ActivitySearchResult {
                totalHits
                hits {
                    ...ActivityHit
                }
                placeCounts {
                    value
                    hits
                }
                themeCounts {
                    value
                    hits
                }
                targetAudienceCount {
                    value
                    hits
                }
            }
        }
    }

    fragment ActivityHit on Activity {
        id
        name
        slug
        highlighted
        place
        videoUrl
        bookingType
        themes {
            slug
            name
        }
        minCapacity
        maxCapacity
        timeslots(startDate: $startDate, endDate: $endDate) {
            ...ActivityPlannerTimeslot
        }
        themes {
            slug
        }
        mainImage {
            transform(config: HERO, format: JPEG) {
                ...ListImage
            }
        }
        webpMainImage: mainImage {
            transform(config: HERO, format: WEBP) {
                ...ListImage
            }
        }
        webpListImage: listImage {
            transform(config: HERO, format: WEBP) {
                ...ListImage
            }
        }
        listImage {
            transform(config: HERO, format: JPEG) {
                ...ListImage
            }
        }
    }

    fragment ActivityPlannerTimeslot on Timeslot {
        id
        allotment
        startDateTime
        endDateTime
        label
        rateGroup {
            canBuyTickets
            releaseHours
            rates {
                id
                name
                price
            }
        }
    }
`;

interface Props {
    amountPerPage: number;
    variant: 'events' | 'excursions';
    ssr?: boolean;
}

export interface ActivitySearchValues {
    themeSlugs: string[];
    placeId: string[];
    view: AgendaView;
    targetAudiences: TargetAudienceEnum[];
    startDate: string;
    endDate: string;
}

const targetAudienceMessages = defineMessages<TargetAudienceEnum>({
    ALL_AGES: { defaultMessage: 'Alle leeftijden' },
    ADULTS: { defaultMessage: 'Volwassenen' },
    CHILDREN: { defaultMessage: 'Kinderen' },
    WHEELCHAIR_ACCESSIBLE: { defaultMessage: 'Rolstoeltoegankelijk' },
    PETS: { defaultMessage: 'Met huisdier' },
});

const variantMessages = {
    events: defineMessages({
        singular: { defaultMessage: 'evenement' },
        plural: { defaultMessage: 'evenementen' },
        notFoundShort: { defaultMessage: 'Geen evenementen gevonden' },
        notFoundLong: { defaultMessage: 'Op deze dag zijn er geen evenementen met deze filters.' },
        title: { defaultMessage: 'Evenementen' },
    }),
    excursions: defineMessages({
        singular: { defaultMessage: 'excursie' },
        plural: { defaultMessage: 'excursies' },
        notFoundShort: { defaultMessage: 'Geen excursies gevonden' },
        notFoundLong: { defaultMessage: 'Op deze dag zijn er geen excursies met deze filters.' },
        title: { defaultMessage: 'Excursies' },
    }),
};

const useImageSizes = () => {
    const { mediaQueries } = useThemeContext();
    return `(min-width: ${mediaQueries.xxl}) 25vw, (min-width: ${mediaQueries.xl}) 25vw, (min-width: ${mediaQueries.s}) 25vw, 100vw`;
};

const determinePeriod = (date: string, view: AgendaView): [string, string] => {
    switch (view) {
        case 'month': {
            const startDate = startOfMonth(new Date(date));
            const endDate = endOfMonth(startDate);
            return [parseToDateString(startDate), parseToDateString(endDate)];
        }
        case 'week': {
            const startDate = new Date(date);
            const endDate = endOfWeek(startDate, weekOpts);
            return [parseToDateString(startDate), parseToDateString(endDate)];
        }
        default:
            return [date, date];
    }
};

const filterKeys: Array<keyof ActivitySearchValues> = ['themeSlugs', 'placeId', 'targetAudiences'];

const Actvities: React.FC<React.PropsWithChildren<Props>> = ({ amountPerPage, ssr = false, variant }) => {
    const { colors } = useThemeContext();
    const intl = useIntl();
    const sizes = useImageSizes();
    const deviceSize = useDeviceSize();
    const shouldAutoSave = deviceSize !== 'mobile';

    const filterModal = useSesame();
    const today = useMemo(() => new Date(), []);
    /** parse params */
    const [params, setParams] = useQueryParams(activitySearchParams);
    const [filtersActive, removeActiveFilters] = useFiltersActive(activitySearchFilterParams);
    const {
        placeId,
        startDate = parseToDateString(today),
        endDate = parseToDateString(addDays(today, variant === 'excursions' ? 7 : 365)),
        targetAudiences = [],
        themeSlugs = [],
        view = 'day',
    } = params;
    /** fetch filters */
    const filtersQuery = useAgendaFiltersQuery({ ssr });
    const onViewChange = useCallback(
        (newView: AgendaView) => {
            if (newView === 'day') {
                const [periodStartDate, periodEndDate] = determinePeriod(
                    parseToDateString(today),
                    newView as AgendaView
                );
                setParams({ startDate: periodStartDate, endDate: periodEndDate, view: newView });
            } else {
                const [periodStartDate, periodEndDate] = determinePeriod(startDate, newView as AgendaView);
                setParams({ startDate: periodStartDate, endDate: periodEndDate, view: newView });
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setParams]
    );
    const initialStartDate = startDate.length > 0 ? startDate : parseToDateString(today);
    const initialEndDate = endDate.length > 0 ? endDate : parseToDateString(addDays(today, 7));

    /** process current filters */
    const paramsInput: ActivitySearchParamsInput = {
        startDate: initialStartDate,
        endDate: initialEndDate,
        placeId,
        targetAudiences: targetAudiences as TargetAudienceEnum[],
        themeSlugs,
        highlighted: variant === 'events',
    };

    /** fetch activities */
    const { data, loading, fetchMore, variables, networkStatus } = useActivitySearchQuery({
        variables: {
            limit: amountPerPage,
            params: paramsInput,
            startDate: initialStartDate,
            endDate: initialEndDate,
            order: variant === 'events' ? ActivitySearchOrderEnum.TimeslotsAsc : ActivitySearchOrderEnum.Random,
        },
        notifyOnNetworkStatusChange: true,
        ssr,
    });

    const canLoadMoreActivities = data && data.activities.totalHits > data.activities.hits.length;

    /** handle formik inputs */
    const handleSubmit = useCallback(
        (values: ActivitySearchValues) => {
            // by changing the params, apollo will refetch the query automagically
            setParams(
                {
                    startDate: values.startDate,
                    endDate: values.endDate,
                    view: values.view,
                    placeId: values.placeId?.[0],
                    targetAudiences: values.targetAudiences,
                    themeSlugs: values.themeSlugs,
                },
                'replaceIn'
            );
        },
        [setParams]
    );
    // TODO use useMemo in order te create the function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleAutoSave = useCallback(debounce(handleSubmit), [handleSubmit]);

    /** parse activities results */
    const { totalHits = 0 } = data?.activities ?? {};

    const activities = data?.activities.hits;

    const activityThemeCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'themeSlugs',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Thema' }),
                filterProperties: filtersQuery.data?.activityThemes ?? [],
                filterResolver: ({ slug, name }) => ({ handle: slug, label: name ?? '' }),
                filterCounts: data?.activities.themeCounts ?? [],
                filterCountKey: 'value',
            }),
        [data, filtersQuery.data, intl]
    );
    const placesFilterCategory: FilterCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'placeId',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Plaats' }),
                filterProperties: filtersQuery.data?.places ?? [],
                filterResolver: ({ id, name }) => ({ handle: id, label: name }),
                filterCounts: data?.activities.placeCounts ?? [],
                filterCountKey: 'value',
            }),
        [data, filtersQuery.data, intl]
    );

    const targetAudienceCategory: FilterCategory = useMemo(() => {
        const targets = Object.entries(targetAudienceMessages).map(([key, value]) => ({
            label: intl.formatMessage(value),
            handle: key,
        }));
        return composeFilterCategory({
            categoryHandle: 'targetAudiences',
            categoryLabel: intl.formatMessage({ defaultMessage: 'Doelgroep' }),
            filterProperties: targets,
            filterCounts: data?.activities.targetAudienceCount ?? [],
            filterCountKey: 'value',
        });
    }, [data, intl]);

    const filterCategories = [activityThemeCategory, placesFilterCategory, targetAudienceCategory];

    /** filters changed */
    const activityFilters = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(params).filter(([key]) => Object.keys(activitySearchFilterParams).includes(key))
            ),
        [params]
    );
    const filtersUnchanged = useMemo(() => Object.values(activityFilters).length === 0, [activityFilters]);
    const resetDate = useCallback(() => {
        onViewChange('day');
    }, [onViewChange]);

    const messages = variantMessages[variant];

    return (
        <Page id="search-page">
            <Formed<ActivitySearchValues>
                skipPrompt
                initialValues={{
                    startDate: initialStartDate,
                    endDate: initialEndDate,
                    view: view as AgendaView,
                    placeId: placeId ? [placeId] : [],
                    themeSlugs,
                    targetAudiences: targetAudiences as TargetAudienceEnum[],
                }}
                handleAutoSave={(values, helpers) => {
                    helpers.setSubmitting(true);
                    handleAutoSave(values);
                }}
                handleSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting }) => {
                    const isLoading = (loading && networkStatus !== NetworkStatus.fetchMore) || isSubmitting;
                    return (
                        <StyledContentWrapper variant="searchList">
                            {variant === 'excursions' && (
                                <AgendaHeader onViewChange={onViewChange} resetDate={resetDate} />
                            )}
                            <Flex flexDirection={['column', null, null, 'row']}>
                                <LeftBar className="gt-m">
                                    <SectionHeader>
                                        <Title style={{ fontWeight: 400 }}>
                                            {totalHits === 1 ? (
                                                <>
                                                    <span style={{ color: colors.secondary['40'] }}>{totalHits}</span>{' '}
                                                    {intl.formatMessage(messages.singular)}
                                                </>
                                            ) : (
                                                <>
                                                    <span style={{ color: colors.secondary['40'] }}>{totalHits}</span>{' '}
                                                    {intl.formatMessage(messages.plural)}
                                                </>
                                            )}
                                        </Title>
                                    </SectionHeader>
                                    {variant === 'excursions' && (
                                        <>
                                            <FilterContainer className="gt-m">
                                                <DatepickerField
                                                    optionalSingleDate
                                                    startName="startDate"
                                                    endName="endDate"
                                                    align="left"
                                                >
                                                    <SearchbarDatepickerInputs
                                                        startLabel={<FormattedMessage defaultMessage="Van" />}
                                                        endLabel={<FormattedMessage defaultMessage="Tot" />}
                                                        withVerticalPadding
                                                        rounded
                                                    />
                                                </DatepickerField>
                                            </FilterContainer>
                                            <Box my={4}>
                                                <Button variant="outline" size="small" onClick={() => resetDate()}>
                                                    <FormattedMessage defaultMessage="Reset" />
                                                </Button>
                                            </Box>
                                            <Divider />
                                        </>
                                    )}
                                    {filtersQuery.loading && <FilterList mt={6} loading />}
                                    {!filtersQuery.loading && (
                                        <>
                                            <SearchFilterTags
                                                shouldAutoSave={shouldAutoSave}
                                                categories={filterCategories}
                                            />
                                            <FilterList categories={filterCategories} />
                                        </>
                                    )}
                                </LeftBar>

                                <SearchBottomBar
                                    allowedActions={['filters']}
                                    filtersActive={filtersActive}
                                    onFiltersClick={filterModal.onOpen}
                                />
                                <SearchFiltersModal
                                    dirtyCheckKeys={filterKeys}
                                    filtersActive={filtersActive}
                                    onClose={filterModal.onClose}
                                    open={filterModal.open}
                                    removeActiveFilters={removeActiveFilters}
                                >
                                    <FilterList categories={filterCategories} />
                                </SearchFiltersModal>

                                <ListingSection>
                                    {!isLoading && totalHits === 0 && (
                                        <section>
                                            <SectionHeader>
                                                <Title>{intl.formatMessage(messages.notFoundShort)}</Title>
                                            </SectionHeader>
                                            <Body>{intl.formatMessage(messages.notFoundLong)}</Body>
                                            {!filtersUnchanged && (
                                                <>
                                                    <br />
                                                    <Button
                                                        style={{
                                                            alignSelf: 'flex-start',
                                                        }}
                                                        variant="outline"
                                                        onClick={removeActiveFilters}
                                                        type="button"
                                                    >
                                                        <FormattedMessage defaultMessage="Filters verwijderen" />
                                                    </Button>
                                                </>
                                            )}
                                        </section>
                                    )}
                                    {isLoading && (
                                        <section>
                                            <SectionHeader>
                                                <Skeleton width="20rem" height="2rem" />
                                            </SectionHeader>
                                            <WideGrid widths={[100, 50, 100 / 3]}>
                                                {[...new Array(6)].map((x, i) => (
                                                    <Cell key={i}>
                                                        <ActivityTile loading />
                                                    </Cell>
                                                ))}
                                            </WideGrid>
                                        </section>
                                    )}
                                    {activities && activities.length > 0 && (
                                        <section>
                                            <SectionHeader>
                                                <Title>{intl.formatMessage(messages.title)}</Title>
                                            </SectionHeader>
                                            <WideGrid widths={[100, 50, 100 / 3]}>
                                                {activities.map((a, i) => (
                                                    <Cell key={i}>
                                                        {variant === 'events' ? (
                                                            <EventHit hit={a} sizes={sizes} />
                                                        ) : (
                                                            <ActivityHit
                                                                hit={a}
                                                                dontPassDate={view !== 'day'}
                                                                sizes={sizes}
                                                            />
                                                        )}
                                                        {i !== activities.length - 1 && (
                                                            <Divider mt="2.4rem" mb={0} className="lt-s" />
                                                        )}
                                                    </Cell>
                                                ))}
                                            </WideGrid>

                                            {canLoadMoreActivities && (
                                                <Flex width={1} justifyContent="center" my={7}>
                                                    <Button
                                                        submitting={networkStatus === NetworkStatus.fetchMore}
                                                        variant="outline"
                                                        onClick={() =>
                                                            fetchMore({
                                                                variables: {
                                                                    ...variables,
                                                                    offset: data?.activities.hits.length ?? 0,
                                                                },
                                                                updateQuery: (prev, { fetchMoreResult }) => {
                                                                    if (
                                                                        !prev.activities ||
                                                                        !fetchMoreResult?.activities
                                                                    ) {
                                                                        return prev;
                                                                    }

                                                                    return {
                                                                        ...prev,
                                                                        activities: {
                                                                            ...prev.activities,
                                                                            hits: [
                                                                                ...prev.activities.hits,
                                                                                ...fetchMoreResult.activities.hits,
                                                                            ],
                                                                        },
                                                                    };
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <FormattedMessage defaultMessage="Meer laden..." />
                                                    </Button>
                                                </Flex>
                                            )}
                                        </section>
                                    )}
                                </ListingSection>
                            </Flex>
                        </StyledContentWrapper>
                    );
                }}
            </Formed>
        </Page>
    );
};

export default Actvities;

const StyledContentWrapper = styled(ContentWrapper)`
    padding-bottom: 8rem;
`;

const SectionHeader = styled(Header)`
    margin: 4rem 0;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        margin: 4rem 0 2.4rem;
    }
`;

const LeftBar = styled(Box)`
    flex-shrink: 0;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 29.6rem;
        padding-right: ${({ theme }) => theme.spacing['60_Large']};

        ${SectionHeader} {
            margin-bottom: 2.4rem;
        }
    }
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
        width: 32.8rem;
    }
`;

const FilterContainer = styled.div`
    display: flex;
    flex-flow: column;

    > span + span {
        margin-top: 1rem;
    }
`;

const ListingSection = styled.section`
    width: 100%;

    & > * + * {
        margin-top: ${cssCalcBetweenViewport(24, 56)};
    }
`;
